import { useMemo } from "react";
import { CalendarDays, Clock } from "lucide-react";

export const TIME_SLOTS = [
  "9:00 AM CT",
  "11:00 AM CT",
  "1:00 PM CT",
  "3:00 PM CT",
] as const;

export type BookingSlot = { date: string; time: string };

/** Next N weekdays (skipping weekends), starting tomorrow. */
export function nextBusinessDays(count = 10, from = new Date()): Date[] {
  const days: Date[] = [];
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  while (days.length < count) {
    cursor.setDate(cursor.getDate() + 1);
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) days.push(new Date(cursor));
  }
  return days;
}

export const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

export function formatSlot(date: string, time: string) {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return `${dt.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  })} at ${time}`;
}

type Props = {
  value: BookingSlot;
  onChange: (slot: BookingSlot) => void;
  error?: string;
};

/**
 * Lightweight consultation scheduler — a rolling window of weekday openings
 * with fixed strategy-session blocks. Selection is confirmed by the team,
 * so no external calendar dependency is required.
 */
export function BookingSlotPicker({ value, onChange, error }: Props) {
  const days = useMemo(() => nextBusinessDays(10), []);

  return (
    <div>
      <div className="flex items-center gap-2 text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
        <CalendarDays className="h-3.5 w-3.5 text-gold" />
        Preferred day
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2" role="radiogroup" aria-label="Preferred day">
        {days.map((d) => {
          const iso = toISODate(d);
          const active = value.date === iso;
          return (
            <button
              key={iso}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange({ ...value, date: iso })}
              className={`shrink-0 border px-4 py-3 text-center transition-colors ${
                active
                  ? "border-gold bg-gold/10 text-white"
                  : "border-border text-silver hover:border-gold/40"
              }`}
            >
              <span className="block text-[10px] uppercase tracking-[0.2em]">
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </span>
              <span className="block font-display text-lg mt-1">{d.getDate()}</span>
              <span className="block text-[10px] uppercase tracking-[0.16em]">
                {d.toLocaleDateString(undefined, { month: "short" })}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-silver text-[11px] uppercase tracking-[0.2em] mt-4 mb-2">
        <Clock className="h-3.5 w-3.5 text-gold" />
        Preferred time
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Preferred time">
        {TIME_SLOTS.map((t) => {
          const active = value.time === t;
          return (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange({ ...value, time: t })}
              className={`border px-3 py-3 text-xs transition-colors ${
                active
                  ? "border-gold bg-gold/10 text-white"
                  : "border-border text-silver hover:border-gold/40"
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>

      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
    </div>
  );
}
